"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultSEPANormalization = defaultSEPANormalization;
exports.SEPATransactionSchema = SEPATransactionSchema;
exports.SEPATransactionMetadataSchema = SEPATransactionMetadataSchema;
const ibantools_1 = require("ibantools");
const zod_1 = require("zod");
const utils_1 = require("./utils");
const SEPA_REG_EX = /^[a-z0-9äößü@&_€+().,/\-\s]+$/i;
function defaultSEPANormalization(value) {
    return value
        .replace('@', '(at)')
        .replace('&', '+')
        .replace('_', '-')
        .replace('ä', 'ae')
        .replace('ö', 'oe')
        .replace('ü', 'ue')
        .replace('ß', 'ss')
        .replace('Ä', 'Ae')
        .replace('Ö', 'Oe')
        .replace('Ü', 'Ue')
        .replace('€', 'EUR');
}
function precisionOf(value) {
    const parts = value.toString().split('.');
    return parts.length > 1 ? parts[1].length : 0;
}
function languageSchema() {
    return zod_1.z.enum(['de', 'en', 'es', 'fr']).default('de');
}
function currencyCodeSchema() {
    return zod_1.z.enum(['EUR', 'CHF']).default('EUR');
}
function amountSchema(translations) {
    return zod_1.z
        .number({
        error: issue => issue.input === undefined
            ? translations.fieldRequired('amount')
            : translations.typeNaN('amount'),
    })
        .gt(0, translations.minNumN(0, 'amount'))
        .refine(value => precisionOf(value) <= 2, {
        message: translations.maxDecimalsN(2, 'amount'),
    });
}
function recipientSchema(translations) {
    return zod_1.z
        .string({ error: () => translations.fieldRequired('recipient') })
        .regex(SEPA_REG_EX, translations.invalidCharsForField('recipient'));
}
function referenceSchema(translations) {
    return zod_1.z
        .string({ error: () => translations.fieldRequired('reference') })
        .min(5, translations.minCharsN(5, 'reference'))
        .max(140, translations.maxCharsN(140, 'reference'))
        .regex(SEPA_REG_EX, translations.invalidCharsForField('reference'))
        .regex(/^((?!test$)).*/i, translations.reservedWord('reference'));
}
function ibanSchema(translations) {
    return zod_1.z.preprocess(value => (typeof value === 'string' ? (0, ibantools_1.electronicFormatIBAN)(value) : value), zod_1.z
        .string({ error: () => translations.fieldRequired('iban') })
        .min(1, translations.fieldRequired('iban'))
        .refine(value => (0, ibantools_1.isValidIBAN)(value), {
        message: translations.invalid('iban'),
    }));
}
function bicSchema(translations) {
    return zod_1.z.preprocess(utils_1.emptyStringToNullTransform, zod_1.z
        .string()
        .refine(value => (0, ibantools_1.isValidBIC)(value), {
        message: translations.invalid('bic'),
    })
        .nullish());
}
function SEPATransactionSchema({ translations, }) {
    return zod_1.z.object({
        amount: amountSchema(translations),
        bic: bicSchema(translations),
        currencyCode: currencyCodeSchema(),
        iban: ibanSchema(translations),
        language: languageSchema(),
        metadata: zod_1.z.object({
            containerId: zod_1.z
                .string({ error: () => translations.fieldRequired('containerId') })
                .min(1, translations.fieldRequired('containerId'))
                .max(128, translations.maxCharsN(128, 'containerId')),
            customerId: zod_1.z
                .string({ error: () => translations.fieldRequired('customerId') })
                .min(1, translations.fieldRequired('customerId'))
                .max(128, translations.maxCharsN(128, 'customerId')),
        }),
        recipient: recipientSchema(translations),
        reference: referenceSchema(translations),
        senderBic: bicSchema(translations),
    });
}
function SEPATransactionMetadataSchema(translations) {
    return zod_1.z.object({
        amount: zod_1.z.preprocess(utils_1.emptyNumberStringToNullTransform, amountSchema(translations).nullish()),
        bic: bicSchema(translations),
        currencyCode: currencyCodeSchema().nullable(),
        iban: zod_1.z.preprocess(value => {
            if (value === '' || value == null) {
                return null;
            }
            return typeof value === 'string' ? (0, ibantools_1.electronicFormatIBAN)(value) : value;
        }, zod_1.z
            .string()
            .refine(value => (0, ibantools_1.isValidIBAN)(value), {
            message: translations.invalid('iban'),
        })
            .nullish()),
        recipient: zod_1.z.preprocess(utils_1.emptyStringToNullTransform, recipientSchema(translations).nullish()),
        reference: zod_1.z.preprocess(utils_1.emptyStringToNullTransform, referenceSchema(translations).nullish()),
    });
}
//# sourceMappingURL=sepa-transaction.js.map